'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';

export default function LandingPage() {
  const [glitch, setGlitch] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setGlitch(true);
      setTimeout(() => setGlitch(false), 150);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col overflow-hidden relative">
      {/* Skip link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-[#FF5F1F] focus:text-black focus:font-mono focus:text-xs focus:font-bold focus:uppercase focus:tracking-widest focus:outline-none"
      >
        Zum Hauptinhalt springen
      </a>

      {/* Noise texture overlay */}
      <div
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.04]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '128px 128px',
        }}
      />

      {/* Scanlines */}
      <div
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.03]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.05) 2px, rgba(255,255,255,0.05) 4px)',
        }}
      />

      {/* Neon glow blobs */}
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <div
          className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #FF5F1F 0%, transparent 70%)' }}
        />
        <div
          className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] rounded-full opacity-8"
          style={{ background: 'radial-gradient(circle, #FF5F1F 0%, transparent 70%)' }}
        />
      </div>

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between px-6 py-4 border-b border-white/10" role="banner">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-[#FF5F1F] rounded-full animate-pulse" aria-hidden="true" />
          <span className="font-mono text-xs tracking-[0.3em] text-white/50 uppercase">SEARCH4 DISTORTION</span>
        </div>
        <nav className="hidden md:flex items-center gap-1" aria-label="Hauptnavigation">
          <Link href="/content-dashboard" className="font-mono text-[11px] tracking-widest text-white/40 hover:text-white transition-colors uppercase px-3 py-1.5 hover:bg-white/5">
            Dashboard
          </Link>
          <Link href="/content-generator" className="font-mono text-[11px] tracking-widest text-white/40 hover:text-white transition-colors uppercase px-3 py-1.5 hover:bg-white/5">
            Generator
          </Link>
          <Link href="/backstage" className="font-mono text-[11px] tracking-widest text-white/40 hover:text-white transition-colors uppercase px-3 py-1.5 hover:bg-white/5">
            Backstage
          </Link>
          <Link href="/brand-vault" className="font-mono text-[11px] tracking-widest text-white/40 hover:text-white transition-colors uppercase px-3 py-1.5 hover:bg-white/5">
            Brand Vault
          </Link>
          <Link href="/visual-lab" className="font-mono text-[11px] tracking-widest text-white/40 hover:text-white transition-colors uppercase px-3 py-1.5 hover:bg-white/5">
            Visual Lab
          </Link>
          <Link href="/export-sync" className="font-mono text-[11px] tracking-widest text-white/40 hover:text-white transition-colors uppercase px-3 py-1.5 hover:bg-white/5">
            Export
          </Link>
        </nav>
        <Link
          href="/login"
          className="font-mono text-xs tracking-widest text-white/40 hover:text-[#FF5F1F] transition-colors uppercase border border-white/10 hover:border-[#FF5F1F]/50 px-3 py-1.5"
        >
          Login →
        </Link>
      </header>

      {/* Main hero */}
      <main id="main-content" className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-16 text-center" role="main">

        {/* Duct tape strip top */}
        <div className="w-full max-w-3xl mb-8 relative">
          <div
            className="h-7 w-full opacity-90 flex items-center justify-center"
            style={{
              background: 'repeating-linear-gradient(135deg, #1a1a1a 0px, #1a1a1a 10px, #111 10px, #111 20px)',
              border: '1px solid #333',
            }}
          >
            <span className="font-mono text-[10px] tracking-[0.5em] text-white/20 uppercase">/// SIGNAL INCOMING ///</span>
          </div>
        </div>

        {/* Hero title */}
        <div className="relative mb-6">
          <h1
            className={`font-display font-black uppercase leading-none tracking-tighter select-none transition-all duration-75 ${glitch ? 'translate-x-[2px]' : ''}`}
            style={{
              fontSize: 'clamp(3.5rem, 12vw, 9rem)',
              color: '#FF5F1F',
              textShadow: glitch
                ? '-3px 0 #00ffff, 3px 0 #ff00ff, 0 0 40px #FF5F1F' :'0 0 60px rgba(255,95,31,0.5), 0 0 120px rgba(255,95,31,0.2)',
              letterSpacing: '-0.03em',
            }}
          >
            SEARCH4
          </h1>
          <h1
            className={`font-display font-black uppercase leading-none tracking-tighter select-none transition-all duration-75 ${glitch ? '-translate-x-[2px]' : ''}`}
            style={{
              fontSize: 'clamp(3.5rem, 12vw, 9rem)',
              color: '#ffffff',
              textShadow: glitch
                ? '3px 0 #FF5F1F, -3px 0 #00ffff' :'0 0 40px rgba(255,255,255,0.1)',
              letterSpacing: '-0.03em',
              marginTop: '-0.1em',
            }}
          >
            DISTORTION
          </h1>

          {/* Glitch bar */}
          {glitch && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'linear-gradient(transparent 40%, rgba(255,95,31,0.15) 40%, rgba(255,95,31,0.15) 42%, transparent 42%)',
              }}
            />
          )}
        </div>

        {/* Duct tape divider */}
        <div className="flex items-center gap-3 mb-8 w-full max-w-xl">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#FF5F1F]/60 to-transparent" />
          <div
            className="px-3 py-1 text-[10px] font-mono tracking-[0.4em] text-black uppercase font-bold"
            style={{ background: '#FF5F1F', clipPath: 'polygon(4px 0%, 100% 0%, calc(100% - 4px) 100%, 0% 100%)' }}
          >
            KI-CONTENT ENGINE
          </div>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#FF5F1F]/60 to-transparent" />
        </div>

        {/* Tagline */}
        <p
          className="font-sans text-white/60 max-w-2xl leading-relaxed mb-4"
          style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.15rem)' }}
        >
          Weil die Welt kein weißes Rauschen braucht.
        </p>
        <p
          className="font-sans text-white/40 max-w-xl leading-relaxed mb-12"
          style={{ fontSize: 'clamp(0.8rem, 2vw, 0.95rem)' }}
        >
          Für alle, die keine Lust mehr auf Standard bei der Content Creation haben.
        </p>

        {/* CTA Button */}
        <Link
          href="/content-generator"
          className="group relative inline-flex items-center gap-4 px-10 py-5 font-mono font-bold uppercase tracking-[0.3em] text-black bg-[#FF5F1F] hover:bg-white transition-all duration-200 text-sm"
          style={{
            clipPath: 'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)',
            boxShadow: '0 0 40px rgba(255,95,31,0.4), 0 0 80px rgba(255,95,31,0.15)',
          }}
        >
          <span className="relative z-10">Lärm filtern</span>
          <svg
            className="relative z-10 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
          {/* Hover glow */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 100%)' }}
          />
        </Link>

        {/* Sub-CTA links */}
        <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
          <Link href="/content-dashboard" className="font-mono text-xs text-white/30 hover:text-white/60 transition-colors tracking-widest uppercase">
            Dashboard
          </Link>
          <span className="text-white/10 font-mono">|</span>
          <Link href="/brand-vault" className="font-mono text-xs text-white/30 hover:text-white/60 transition-colors tracking-widest uppercase">
            Brand Vault
          </Link>
          <span className="text-white/10 font-mono">|</span>
          <Link href="/visual-lab" className="font-mono text-xs text-white/30 hover:text-white/60 transition-colors tracking-widest uppercase">
            Visual Lab
          </Link>
          <span className="text-white/10 font-mono">|</span>
          <Link href="/backstage" className="font-mono text-xs text-white/30 hover:text-white/60 transition-colors tracking-widest uppercase">
            Backstage
          </Link>
          <span className="text-white/10 font-mono">|</span>
          <Link href="/content-editor" className="font-mono text-xs text-white/30 hover:text-white/60 transition-colors tracking-widest uppercase">
            Editor
          </Link>
          <span className="text-white/10 font-mono">|</span>
          <Link href="/export-sync" className="font-mono text-xs text-white/30 hover:text-white/60 transition-colors tracking-widest uppercase">
            Export & Sync
          </Link>
        </div>

        {/* Duct tape strip bottom */}
        <div className="w-full max-w-3xl mt-16">
          <div
            className="h-7 w-full opacity-90 flex items-center justify-center"
            style={{
              background: 'repeating-linear-gradient(135deg, #1a1a1a 0px, #1a1a1a 10px, #111 10px, #111 20px)',
              border: '1px solid #333',
            }}
          >
            <span className="font-mono text-[10px] tracking-[0.5em] text-white/20 uppercase">/// 14 TAGE CONTENT · 5 PLATTFORMEN · 1 SESSION ///</span>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 px-6 py-4 flex items-center justify-between" role="contentinfo">
        <span className="font-mono text-[10px] text-white/20 tracking-widest uppercase">© 2025 SEARCH4 DISTORTION</span>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-[#FF5F1F] animate-pulse" />
          <span className="font-mono text-[10px] text-white/20 tracking-widest uppercase">SYSTEM ONLINE</span>
        </div>
      </footer>
    </div>
  );
}
